type FilterPropsType = {
    entities: any,
    filters: any
};

/** Ids of the company profile (or of a manual selection) that requirements are compared against. */
export type RequirementIdsType = {
    vehicleTypeIds: number[],
    equipmentIds: number[],
    licenceCertificateIds: number[],
    sustainabilityIds: number[]
};

/**
 * Same rule as the API (watchlist matching): no requirement -> ok; vehicle types must overlap;
 * equipment and certificates must be a subset of the profile; an empty profile category is not filtered.
 * Sustainability is strict: when `sustainabilityIds` is given (even empty) ALL of the entity's sustainability
 * requirements must be in it. Leave it out (undefined) to not filter on sustainability, e.g. for an
 * empty manual selection. The profile's ids include Fair Transport when the company is a member.
 */
export function meetsRequirements(entity: any, profile: Partial<RequirementIdsType>) {
    const vehicleTypeIds: number[] = entity.vehicleTypeIds ?? [];
    const equipmentIds: number[] = entity.equipmentIds ?? [];
    const licenceCertificateIds: number[] = entity.licenceCertificateIds ?? [];
    const profileVehicleTypeIds = profile.vehicleTypeIds ?? [];
    const profileEquipmentIds = profile.equipmentIds ?? [];
    const profileLicenceCertificateIds = profile.licenceCertificateIds ?? [];
    const sustainabilityIds: number[] = entity.sustainabilityIds ?? [];

    return (
        vehicleTypeIds.length === 0
        || profileVehicleTypeIds.length === 0
        || vehicleTypeIds.some((id) => profileVehicleTypeIds.includes(id))
    ) && (
        profileEquipmentIds.length === 0
        || equipmentIds.every((id) => profileEquipmentIds.includes(id))
    ) && (
        profileLicenceCertificateIds.length === 0
        || licenceCertificateIds.every((id) => profileLicenceCertificateIds.includes(id))
    ) && (
        !profile.sustainabilityIds
        || sustainabilityIds.every((id) => profile.sustainabilityIds?.includes(id))
    );
}

/** True when the market filter has requirement selections made by hand (not the fulfil-profile default). */
export function hasManualRequirements(filters: any): boolean {
    return [filters?.vehicleTypeIds, filters?.equipmentIds, filters?.licenceCertificateIds, filters?.sustainabilityIds]
        .some((ids) => Array.isArray(ids) && ids.length > 0);
}

/** Filters that make a watchlist's own hits: geography/publisher come from the API's matchedWatchlistIds. */
export function watchlistToMarketFilters(watchlist: any) {
    return {
        ...watchlist,
        watchlistId: watchlist.id,
        routeId: null,
        publisherTenantId: null
    };
}

/**
 * Route filter. routeMatchMode 'touches' (default): pickup OR delivery in the route; 'within': both.
 * "Inside our routes" (insideRoutes) is the same rule over all the company's routes.
 */
function matchesRoute(e: any, f: any) {
    const pickup: number[] = e.pickupRouteIds ?? [];
    const delivery: number[] = e.deliveryRouteIds ?? [];
    const within = f.routeMatchMode === 'within';
    if (f.routeId) {
        const p = pickup.includes(f.routeId);
        const d = delivery.includes(f.routeId);
        if (!(within ? p && d : p || d)) return false;
    }
    if (f.insideRoutes) {
        const p = pickup.length > 0;
        const d = delivery.length > 0;
        if (!(within ? p && d : p || d)) return false;
    }
    return true;
}

export function filterMarketEntities({ entities, filters: f }: FilterPropsType) {
    return typeof f !== 'object' ? entities
        : entities.filter((e: any) => (
            e.type === 'CAP' || (f.isJob && (e.type !== 'DHL' || f.isDhl !== false))
        ) && (
            f.isCapacity || (e.type !== 'CAP')
        ) && (
            !f.watchlistId || (e.matchedWatchlistIds ?? []).includes(Number(f.watchlistId))
        ) && (
            matchesRoute(e, f)
        ) && (
            !f.publisherTenantId || e.publisherTenantId === f.publisherTenantId
        ) && (
            meetsRequirements(e, {
                vehicleTypeIds: f.vehicleTypeIds ?? [],
                equipmentIds: f.equipmentIds ?? [],
                licenceCertificateIds: f.licenceCertificateIds ?? [],
                sustainabilityIds: f.sustainabilityIds?.length ? f.sustainabilityIds : undefined
            })
        ) && (
            !f.fulfilsProfile || meetsRequirements(e, f.fulfilsProfile)
        ) && (
            !f.firstPickupDate || (e.firstPickupDate >= f.firstPickupDate)
        ) && (
            !f.lastDeliveryDate || (e.lastDeliveryDate <= f.lastDeliveryDate)
        ) && (
            !f.minGrossWeight || (e.grossWeight >= f.minGrossWeight)
        ) && (
            !f.maxGrossWeight || (e.grossWeight <= f.maxGrossWeight)
        ) && (
            !f.minChargeableWeight || (e.chargeableWeight >= f.minChargeableWeight)
        ) && (
            !f.maxChargeableWeight || (e.chargeableWeight <= f.maxChargeableWeight)
        ) && (
            !f.minVolume || (e.volume >= f.minVolume)
        ) && (
            !f.maxVolume || (e.volume <= f.maxVolume)
        ) && (
            !f.minLoadingMeters || (e.loadingMeters >= f.minLoadingMeters)
        ) && (
            !f.maxLoadingMeters || (e.loadingMeters <= f.maxLoadingMeters)
        ) && (
            !f.minPalletPlaces || (e.palletPlaces >= f.minPalletPlaces)
        ) && (
            !f.maxPalletPlaces || (e.palletPlaces <= f.maxPalletPlaces)
        ) && (
            !f.minPallets || (e.pallets >= f.minPallets)
        ) && (
            !f.maxPallets || (e.pallets <= f.maxPallets)
        // ) && (
        //     !f.minPieces || (e.pieces >= f.minPieces)
        // ) && (
        //     !f.maxPieces || (e.pieces <= f.maxPieces)
        ) && (
            !f.groupId || (e.groupIds.some((gid: number) => gid === f.groupId))
        ) && (
            !f.hasListPrice || e.type === 'CAP' || e.listPrice !== null
        ) && (
            !f.hasOffer || e.type === 'CAP' || !!e.offer
        ) && (
            !f.takesOffer || e.type === 'CAP' || (
                e.type === 'LL' && (e.expiresAt === null || e.expiresAt > (new Date()).toISOString())
            )
        ) && (
            !f.locationQuery || (
                e.pickupCity.toLowerCase().includes(f.locationQuery.toLowerCase())
                || e.deliveryCity.toLowerCase().includes(f.locationQuery.toLowerCase())
            )
        ));
}
